import { Button, Collapse, Flex, Text, useDisclosure } from '@chakra-ui/react';
import React from 'react';
import { Listing } from 'src/api/v1-api';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import { useAuth } from 'src/utils/auth';
import { IoAnalyticsOutline, IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { formatToLocalDateTime } from 'src/utils/common';

interface ListingAnalyticsProps {
  listing: Listing | null;
}

const ListingAnalytics = ({ listing }: ListingAnalyticsProps) => {
  const { marketplace } = useMarketplaceContext();
  const { hasStaff, hasAdmin, user } = useAuth();
  const { isOpen, onToggle } = useDisclosure();

  const isAdmin = hasAdmin(marketplace?.id);
  const isStaffUser = hasStaff();
  const isSeller = listing?.marketplace_account?.user?.id === user?.id;

  const shouldShowAnalytics = () => {
    const analyticsSetting = listing?.category?.functional?.display_listing_analytics_enum;

    if (!analyticsSetting || analyticsSetting === 'off') {
      return false;
    }

    if (analyticsSetting === 'only_admins') {
      return isAdmin || isStaffUser;
    }

    if (analyticsSetting === 'admins_and_seller') {
      return isAdmin || isStaffUser || isSeller;
    }

    return false;
  };

  if (!shouldShowAnalytics() || !listing) {
    return null;
  }

  return (
    <Flex direction={'column'} gap={8}>
      <Button
        width={'fit-content'}
        variant="outline"
        size={'sm'}
        leftIcon={<IoAnalyticsOutline />}
        rightIcon={isOpen ? <IoChevronUp /> : <IoChevronDown />}
        onClick={onToggle}
      >
        Analytics
      </Button>
      <Collapse in={isOpen}>
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          align={{ lg: 'center' }}
          gap={{ base: 2, lg: 4 }}
          wrap={'wrap'}
        >
          {!listing?.analytics.views &&
          !listing?.analytics.cta_clicks &&
          !listing?.analytics.submitted_leads ? (
            <Text fontSize="sm" fontWeight={600}>
              No views yet
            </Text>
          ) : (
            <>
              <Flex direction="row" gap={2} align="center">
                <Text fontSize="sm" fontWeight={600}>
                  Views:
                </Text>
                {listing?.analytics.views}
              </Flex>
              <Flex direction="row" gap={2} align="center">
                <Text fontSize="sm" fontWeight={600}>
                  Call-To-Action Clicks:
                </Text>
                {listing?.analytics.cta_clicks}
              </Flex>
              {(isAdmin || isStaffUser) && listing.category?.type === 'leads' && (
                <Flex direction="row" gap={2} align="center">
                  <Text fontSize="sm" fontWeight={600}>
                    Submitted leads:
                  </Text>
                  {listing?.analytics.submitted_leads}
                </Flex>
              )}
              <Flex direction="row" gap={2} align="center">
                <Text fontSize="sm" fontWeight={600}>
                  Analytics Data Last Updated:
                </Text>
                {formatToLocalDateTime(listing?.analytics.updated_at)}
              </Flex>
            </>
          )}
        </Flex>
      </Collapse>
    </Flex>
  );
};

export default ListingAnalytics;
